import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
    MatListModule,
    MatSelectionList,
    MatSelectionListChange,
} from '@angular/material/list';
import {
    MatPaginator,
    MatPaginatorModule,
    PageEvent,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VisibleDirective } from 'src/app/directives/visible.directive';
import { StaffUtilizationViewComponent } from './staff-utilization-view/staff-utilization-view.component';
import { Client } from 'src/app/models/client.model';
import { Project } from 'src/app/models/project-model';
import { Page } from 'src/app/models/page.model';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { AppStateService } from 'src/app/services/app-state-service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ClientService } from 'src/app/services/client.service';
import { ProjectsService } from 'src/app/services/projects.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
    CLIENTID,
    COUNT,
    NAME,
    OFFSET,
    STATUS,
} from 'src/app/constants/queryParamConstants';
import { ACTIVE, INACTIVE } from 'src/app/constants/statusConstant';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import {
    COUNT_ALL,
    ITEM_COUNT,
    OFFSET_START,
} from 'src/app/constants/searchConstants';

@Component({
    selector: 'app-staff-utilization',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatSlideToggleModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatInputModule,
        MatListModule,
        MatPaginatorModule,
        VisibleDirective,
        MatCardModule,
        MatIconModule,
        StaffUtilizationViewComponent,
    ],
    templateUrl: './staff-utilization.component.html',
    styleUrl: './staff-utilization.component.scss',
})
export class StaffUtilizationComponent implements OnInit {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild('projectSelectionList') projectSelectionList: MatSelectionList;
    @ViewChild(StaffUtilizationViewComponent)
    staffUtilizationView: StaffUtilizationViewComponent;
    clients: Client[] = [];
    selectedClient: Client;
    projectSelected = false;
    projectStatus = true;
    loadingProjects = false;
    searchForm = new FormGroup({
        search: new FormControl<string>(null),
        clientId: new FormControl<string>(null),
    });
    canViewClient: boolean = false;
    page = new Page<Project>({
        countRequested: 10,
        offsetRequested: 0,
        items: [],
        totalCount: 0,
    });
    delay: number = 300;
    selectedProject: Project;
    pageIndex = 0;
    queryParams = new Map<string, string | number>();

    constructor(
        private projectService: ProjectsService,
        private clientService: ClientService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private appStateService: AppStateService,
        private snackBar: UniversalSnackbarService,
    ) {}

    ngOnInit(): void {
        const queryParamClientId =
            this.activatedRoute.snapshot.queryParamMap.get(CLIENTID);

        this.populateFilters();

        this.searchForm.controls.clientId.valueChanges
            .pipe(debounceTime(this.delay), distinctUntilChanged())
            .subscribe(() => {
                this.updateUrl();
                this.setQueryParams();
                this.changePageIndex(0);
            });
        const clientParams = new Map<string, string | number>();
        clientParams.set(COUNT, COUNT_ALL);
        clientParams.set(STATUS, ACTIVE);
        this.clientService
            .get([], clientParams)
            .subscribe((response: Page<Client>) => {
                this.clients = response.items;
                this.selectedClient = this.clients.find((c) => {
                    return c.id == this.searchForm.controls.clientId.value;
                });

                if (queryParamClientId) {
                    this.searchForm.controls.clientId.setValue(
                        queryParamClientId,
                    );
                } else {
                    this.searchForm.controls.clientId.setValue(
                        this.clients[0].id,
                    );
                }
            });

        this.searchForm.controls.search.valueChanges
            .pipe(debounceTime(this.delay))
            .subscribe(() => {
                this.getProjects();
            });
    }

    protected getProjects(): void {
        this.loadingProjects = true;
        this.setQueryParams();
        this.projectService.get([], this.queryParams).subscribe({
            next: (response: Page<Project>) => {
                this.page = response;
                this.loadingProjects = false;
            },
            error: () => {
                this.snackBar.open(MSG.DATA_F);
                this.loadingProjects = false;
            },
        });
    }

    private setQueryParams(): void {
        this.queryParams.set(COUNT, ITEM_COUNT);

        if (this.projectStatus) {
            this.queryParams.set(STATUS, ACTIVE);
        } else {
            this.queryParams.set(STATUS, INACTIVE);
        }

        if (this.pageIndex) {
            this.queryParams.set(OFFSET, this.pageIndex * ITEM_COUNT);
        } else {
            this.queryParams.set(OFFSET, OFFSET_START);
        }

        if (
            this.searchForm.controls.search.value &&
            this.searchForm.controls.search.value !== ''
        ) {
            this.queryParams.set(NAME, this.searchForm.controls.search.value);
        }

        if (this.searchForm.controls.search.value === '') {
            if (this.queryParams.has(NAME)) {
                this.queryParams.delete(NAME);
            }
        }

        if (this.searchForm.controls.clientId.value) {
            this.queryParams.set(
                CLIENTID,
                this.searchForm.controls.clientId.value,
            );
        }

        this.updateUrl();
    }

    private updateUrl(): void {
        const queryParams: Params = {};

        if (this.projectStatus !== null && this.projectStatus !== undefined) {
            queryParams[STATUS] = this.projectStatus ? ACTIVE : INACTIVE;
        }

        const nameValue = this.searchForm.controls.search.value;
        if (nameValue) {
            queryParams[NAME] = nameValue;
        }

        const clientId = this.searchForm.controls.clientId.value;
        if (clientId) {
            queryParams[CLIENTID] = clientId;
        }

        void this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
        });
    }

    private populateFilters(): void {
        const params: Params = this.activatedRoute.snapshot.queryParams;

        if (STATUS in params) {
            this.projectStatus =
                (params[STATUS] as string).toUpperCase() !== INACTIVE;
        }

        if (NAME in params) {
            this.searchForm.controls.search.setValue(params[NAME] as string);
        }

        if (CLIENTID in params) {
            this.searchForm.controls.clientId.setValue(
                params[CLIENTID] as string,
            );
        }
    }

    protected changePage(event: PageEvent): void {
        this.changePageIndex(event.pageIndex);
    }

    protected changePageIndex(index: number) {
        this.pageIndex = index;
        this.getProjects(); // Gets the projects on the initial load
    }

    protected onClientRoute(clientId: string): void {
        this.appStateService.title$.next('Clients');
        void this.router.navigate(['clients'], {
            queryParams: { clientId: clientId },
        });
    }

    protected clientChange() {
        this.projectSelected = false;
    }

    protected toggleActiveInactive(): void {
        this.projectStatus = !this.projectStatus;
        this.changePageIndex(0);
    }

    protected onLoadingProjectTasks(value: boolean): void {
        if (!value) {
            this.loadingProjects = false;
            this.getProjects();
        } else {
            this.loadingProjects = true;
        }
    }

    protected projectSelectionChange(event: MatSelectionListChange): void {
        this.staffUtilizationView.editingTasks = false;
        if (!this.projectSelected) {
            this.projectSelected = true;
        }
        this.projectService
            .get([(event.options[0].value as Project).id])
            .subscribe((response: Project) => {
                this.loadingProjects = false;
                this.staffUtilizationView.setProject(response);
            });
    } // add event to selection list to pass project like they do in the projects componenet
}
